/**
 * Cloudflare Turnstile.
 *
 * WHY THIS AND NOT A CAPTCHA
 * Turnstile is almost always invisible: it decides from passive browser signals
 * rather than asking anyone to identify traffic lights. That matters here more
 * than usual, because the person filling this form may be on a cheap phone, on
 * 2G, in bright sunlight, possibly in a hurry, and quite possibly nervous. A
 * puzzle at the end of that form is a report that does not get filed.
 *
 * It is also the only third-party script the public portal loads. It is
 * accepted because Cloudflare already serves this page — so no new party learns
 * anything — and because it is the layer that makes the whole endpoint viable
 * without us ever looking at the visitor's IP.
 *
 * DEV: with no site key configured the widget is stubbed and a notice is shown,
 * so the form still works offline. The edge function in production always
 * verifies server-side; see functions/lib/intake.ts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
const DEV_STUB_TOKEN = 'dev-stub-token';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      language?: string;
    },
  ) => string;
  reset: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function useTurnstile() {
  const isStubbed = !SITE_KEY;
  const [token, setToken] = useState<string | null>(isStubbed ? DEV_STUB_TOKEN : null);

  const reset = useCallback(() => {
    if (isStubbed) {
      setToken(DEV_STUB_TOKEN);
      return;
    }
    setToken(null);
    window.turnstile?.reset();
  }, [isStubbed]);

  return { token, setToken, reset, siteKey: SITE_KEY, isStubbed };
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export function Turnstile({
  siteKey,
  isStubbed,
  onToken,
}: {
  siteKey: string;
  isStubbed: boolean;
  onToken: (token: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const rendered = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isStubbed || rendered.current || !ref.current) return;
    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        rendered.current = true;
        window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (tok) => onToken(tok),
          // A token expires after a few minutes. Someone writing a careful
          // description can easily take longer than that, so clearing it here
          // and re-challenging is better than a confusing failure on submit.
          'expired-callback': () => onToken(null),
          'error-callback': () => {
            onToken(null);
            setFailed(true);
          },
          theme: 'auto',
          language: i18n.language === 'ur' ? 'ur' : 'en',
        });
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
    };
  }, [siteKey, isStubbed, onToken, i18n.language]);

  if (isStubbed) {
    return (
      <p
        className="rounded-md p-3 text-sm"
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--warning)' }}
      >
        {t('report.turnstileStubbed')}
      </p>
    );
  }

  return (
    <div>
      <div ref={ref} />
      <div aria-live="polite">
        {failed && (
          <p className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>
            {t('report.error.turnstileUnavailable')}
          </p>
        )}
      </div>
    </div>
  );
}
